import React from 'react';
import { useTranslation } from 'react-i18next';
import path from 'path';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { GridContainer } from 'formlabs-web-components/Grid';
import { Button, Link } from 'formlabs-web-components/Link';

import { formatPrice } from '../../utils';
import { Breadcrumbs, StoreMenu } from '../navigation';
import ProductList from './ProductList';
import ContactSales from '../content/ContactSales';

import './ProductCategoryPlugin.module.css';

const ProductCategoryPlugin = ({
  baseUrl,
  disableDirectSales,
  categories,
  category,
  classes,
  menu,
  products,
  currency,
  language,
  onlyDisplayBundles = false,
  isWholesale = false
}) => {
  const [t] = useTranslation();

  // HACK Display special form3 cta on form-2 category
  if (category.id === 30) {
    const form3Prices = {
      EUR: 3299,
      CHF: 4360,
      GBP: 2900,
      USD: 3499
    };

    // Fallback to USD price on unknown currency
    const form3Price = form3Prices.hasOwnProperty(currency)
      ? formatPrice(form3Prices[currency], language, currency)
      : formatPrice(form3Prices['USD'], 'en', 'USD');

    const form3CTA = {
      sku: 'INVALID',
      manualEntry: true,
      type_id: 'bundle',
      specialContent: (
        <p>
          {t(
            'The new Form 3 and Form 3L use advanced Low Force Stereolithography (LFS)™ technology to deliver incredible print quality and printer reliability, with resin cartridges, build platforms, and post-processing accessories cross-compatible with the Form 2.'
          )}{' '}
          <Link to={t('/3d-printers/form-3/tech-specs/')}>
            {t('Compare Formlabs SLA models')}
          </Link>
          .
        </p>
      ),
      cta: (
        <Button to={`${baseUrl}form-3/`}>{t('Explore Form 3 Packages')}</Button>
      ),
      url: `form-3`,
      attributes: {
        image: path.join(process.env.PUBLIC_PATH, '/form3collection.png')
      },
      name: t('The Next Generation of Industrial 3D Printing'),
      price: t('Starting at {{price}}', {
        price: form3Price
      })
    };

    if (products.length) {
      // Hack: There should be only one first level product in this category: PKG-F2-REFURB, display it wider
      products[0].displayWide = true;
    }

    products.push(form3CTA);
  }

  // Add special teaser for form3b
  let form3bBasicTeaser = null;
  if (category.name === 'Form 3B') {
    form3bBasicTeaser = (
      <div styleName="teaser">
        <div>
          <div styleName="teaser-image">
            <img
              src={path.join(process.env.PUBLIC_PATH, '/Form-3B-basic.png')}
              alt={t('Form 3B Basic')}
            />
          </div>
          <div styleName="teaser-name">
            <h3>{t('Form 3B Basic')}</h3>
            <p>{t('Contact sales for quote')}</p>
          </div>
        </div>
        <div>
          <div styleName="teaser-content">
            <strong>{t('Includes')}:</strong>
            <p>{t('Standalone Form 3B Printer')}</p>
          </div>
          <div styleName="teaser-action">
            <ContactSales
              linkText={t('Contact Sales')}
              displayType="button"
              buttonColor="light"
            />
          </div>
        </div>
      </div>
    );
  }

  if (onlyDisplayBundles) {
    return (
      <div styleName="category" className={classes}>
        <GridContainer>
          <ProductList
            hideHeader
            baseUrl={baseUrl}
            category={category}
            products={products}
            key={category.id}
            disableDirectSale={disableDirectSales}
          />
          {form3bBasicTeaser}
        </GridContainer>
      </div>
    );
  }

  return (
    <div styleName="category" className={classes}>
      <StoreMenu baseUrl={baseUrl} menu={menu} isWholesale={isWholesale} />
      <div styleName="menu">
        <Breadcrumbs
          entries={[
            { name: t('Store'), url: baseUrl },
            { name: category.name }
          ]}
        />
      </div>
      <GridContainer>
        <ProductList
          hideHeader
          baseUrl={baseUrl}
          category={category}
          products={products}
          key={category.id}
          disableDirectSale={disableDirectSales}
        />
        {form3bBasicTeaser}
        {categories && categories.length ? (
          <div>
            {categories.map(cat => (
              <ProductList
                key={cat.id}
                baseUrl={baseUrl}
                limit={4}
                highlightBundles={false}
                category={cat}
                products={cat.product_children}
                disableDirectSale={disableDirectSales}
              />
            ))}
          </div>
        ) : null}
      </GridContainer>
    </div>
  );
};

export default compose(
  connect(state => {
    return {
      currency: state.i18n.currency,
      language: state.i18n.language
    };
  }),
  DisplayComponent
)(ProductCategoryPlugin);
